import type { VisitCounselView, StructuredDepartureAddress } from './visit-counsel.types';
import type {
  MovingSiteView,
  MovingMethodView,
  Packing,
  CustomerRequests,
  MovingMethodRequest,
} from './intake.types';

/** MovingMethodView → MovingMethodRequest (폼용) */
function toMovingMethodRequest(m?: MovingMethodView | null): MovingMethodRequest | null {
  if (!m) return null;
  const attrs = m.attributes ?? {};
  return {
    type: m.type,
    attributes: Object.keys(attrs).length ? attrs : null,
  };
}

/**
 * 출발지 데이터 - sessions.intake 우선, 없으면 counsel 루트 필드
 */
export function getIntakeDeparture(counsel: VisitCounselView | null | undefined): {
  address: StructuredDepartureAddress | null;
  supplyArea: number | '';
  hasElevator: boolean | null;
  movingMethod: MovingMethodRequest | null;
} | null {
  if (!counsel) return null;
  const intake = counsel.sessions?.intake?.departure;
  return {
    address: intake?.address ?? counsel.departureAddress ?? null,
    supplyArea: intake?.supplyArea ?? counsel.supplyArea ?? '',
    hasElevator: intake?.hasElevator ?? counsel.departureHasElevator ?? null,
    movingMethod: toMovingMethodRequest(
      intake?.movingMethod ?? counsel.departureMovingMethod ?? null
    ),
  };
}

/**
 * 도착지 데이터 - sessions.intake 우선, 없으면 counsel 루트 필드
 */
export function getIntakeArrival(counsel: VisitCounselView | null | undefined): {
  address: StructuredDepartureAddress | null;
  supplyArea: number | '';
  hasElevator: boolean | null;
  movingMethod: MovingMethodRequest | null;
} | null {
  if (!counsel) return null;
  const intake = counsel.sessions?.intake?.arrival;
  return {
    address: intake?.address ?? counsel.arrivalAddress ?? null,
    supplyArea: intake?.supplyArea ?? counsel.arrivalSupplyArea ?? '',
    hasElevator: intake?.hasElevator ?? counsel.arrivalHasElevator ?? null,
    movingMethod: toMovingMethodRequest(
      intake?.movingMethod ?? counsel.arrivalMovingMethod ?? null
    ),
  };
}

/**
 * 포장방식 - sessions.intake 우선
 */
export function getIntakePacking(
  counsel: VisitCounselView | null | undefined
): Packing | null | undefined {
  if (!counsel) return undefined;
  return counsel.sessions?.intake?.packing ?? counsel.packing ?? null;
}

/**
 * 고객요청사항 - sessions.intake 우선
 */
export function getIntakeCustomerRequests(
  counsel: VisitCounselView | null | undefined
): CustomerRequests | null | undefined {
  if (!counsel) return undefined;
  return counsel.sessions?.intake?.customerRequests ?? counsel.customerRequests ?? null;
}

/**
 * 이사일 - sessions.intake 우선
 */
export function getIntakeMovingDate(
  counsel: VisitCounselView | null | undefined
): string | null | undefined {
  if (!counsel) return undefined;
  return counsel.sessions?.intake?.movingDate ?? counsel.movingDate ?? null;
}

/**
 * 이사 유형 인테이크 — 리뷰·체크리스트용 (1개 이상 선택됐는지).
 */
export function hasIntakeMovingTypeRecorded(counsel: VisitCounselView | null | undefined): boolean {
  const intake = counsel?.movingTypeIntake;
  if (!intake) return false;
  return Array.isArray(intake.selectedTypes) && intake.selectedTypes.length > 0;
}

const movingTypesProgressStepKey = (counselId: string) => `mv:moving-types-step:${counselId}`;

/** 이사 유형 progress sub-stage 를 저장 완료한 뒤 호출 (빈 선택 포함). */
export function markMovingTypesProgressStepCompleted(counselId: string): void {
  try {
    sessionStorage.setItem(movingTypesProgressStepKey(counselId), '1');
  } catch {
    // SSR / storage 비활성
  }
}

function hasCustomerRequestsRecordedOnCounsel(
  counsel: VisitCounselView | null | undefined
): boolean {
  const reqs = counsel?.customerRequests;
  if (!reqs) return false;
  return Array.isArray(reqs.questions) && Array.isArray(reqs.paidQuoteRequests);
}

/**
 * 상담 진행하기 플로우에서 이사 유형 화면을 거쳤는지.
 *
 * counsel.movingTypeIntake 가 빈 객체(`selectedTypes: []`)만 있는 경우는 API 기본값일 수
 * 있어 step 완료로 보지 않는다. PUT moving-type 이후 sessionStorage / sessions.intake mirror /
 * selectedTypes ≥ 1 / intake COMPLETED / customerRequests 기록 으로 완료 판정.
 *
 * 빈 selectedTypes 를 PUT 한 뒤 projection 이 `moving_type_intake_json = NULL` 로 떨어지는
 * 케이스(019e5914 등)는 intake COMPLETED 또는 customerRequests 가 남아 있으면 step 완료로 본다.
 */
export function hasCompletedMovingTypesProgressStep(
  counsel: VisitCounselView | null | undefined
): boolean {
  if (!counsel) return false;

  try {
    if (sessionStorage.getItem(movingTypesProgressStepKey(counsel.id)) === '1') return true;
  } catch {
    // ignore
  }

  if (counsel.sessions?.intake?.status === 'COMPLETED') return true;
  if (hasCustomerRequestsRecordedOnCounsel(counsel)) return true;

  if (counsel.sessions?.intake?.movingTypeIntake != null) return true;

  const root = counsel.movingTypeIntake;
  return Array.isArray(root?.selectedTypes) && root.selectedTypes.length > 0;
}

/**
 * 표시용 출발지 주소 - sessions.intake.departure.address ?? counsel.departureAddress
 */
export function getIntakeDepartureAddress(
  counsel: VisitCounselView | null | undefined
): StructuredDepartureAddress | null | undefined {
  if (!counsel) return undefined;
  return counsel.sessions?.intake?.departure?.address ?? counsel.departureAddress ?? null;
}

/**
 * 표시용 도착지 주소 - sessions.intake.arrival.address ?? counsel.arrivalAddress
 */
export function getIntakeArrivalAddress(
  counsel: VisitCounselView | null | undefined
): StructuredDepartureAddress | null | undefined {
  if (!counsel) return undefined;
  return counsel.sessions?.intake?.arrival?.address ?? counsel.arrivalAddress ?? null;
}

// ============================================================================
// 인테이크 세션 진행 상태용 (sessions.intake만 사용, counsel 루트 폴백 없음)
// ============================================================================

/**
 * 인테이크 세션 진행 상태 - 출발지 완료 여부 (sessions.intake.departure만 사용)
 */
export function hasIntakeSessionDeparture(counsel: VisitCounselView | null | undefined): boolean {
  const dep = counsel?.sessions?.intake?.departure;
  return !!(dep?.address ?? (dep?.supplyArea != null && dep.supplyArea > 0));
}

/**
 * 인테이크 세션 진행 상태 - 도착지 완료 여부 (sessions.intake.arrival만 사용)
 */
export function hasIntakeSessionArrival(counsel: VisitCounselView | null | undefined): boolean {
  const arr = counsel?.sessions?.intake?.arrival;
  return !!(arr?.address ?? (typeof arr?.supplyArea === 'number' && arr.supplyArea > 0));
}

/**
 * 인테이크 세션 진행 상태 - 이사일 완료 여부 (sessions.intake.movingDate만 사용)
 */
export function hasIntakeSessionMovingDate(counsel: VisitCounselView | null | undefined): boolean {
  return !!counsel?.sessions?.intake?.movingDate;
}

/**
 * 인테이크 세션 진행 상태 - 포장방식 완료 여부 (sessions.intake.packing만 사용)
 */
export function hasIntakeSessionPacking(counsel: VisitCounselView | null | undefined): boolean {
  return !!counsel?.sessions?.intake?.packing?.method;
}

/**
 * 인테이크 세션 진행 상태 - 고객요청사항 완료 여부 (sessions.intake.customerRequests만 사용)
 * 빈 배열도 "기록함"으로 간주 (선택사항이므로)
 */
export function hasIntakeSessionCustomerRequests(
  counsel: VisitCounselView | null | undefined
): boolean {
  const cr = counsel?.sessions?.intake?.customerRequests;
  return cr != null;
}
